import { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Share2, FolderOpen, BarChart3, CreditCard,
  Shield, Moon, Sun, LogOut, Menu, X, Zap, ShoppingBag,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/dashboard", label: "Tableau de bord", Icon: LayoutDashboard },
  { to: "/studio", label: "Studio IA", Icon: Sparkles },
  { to: "/catalog", label: "Catalogue", Icon: ShoppingBag },
  { to: "/hub", label: "Hub de diffusion", Icon: Share2 },
  { to: "/library", label: "Bibliothèque", Icon: FolderOpen },
  { to: "/analytics", label: "Analytiques", Icon: BarChart3 },
  { to: "/billing", label: "Facturation", Icon: CreditCard },
];

export const DashboardLayout = () => {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="px-5 h-16 flex items-center border-b border-border">
        <Logo size="sm" onClick={() => navigate("/dashboard")} />
      </div>

      <div className="px-4 py-4">
        <div className="mg-glass rounded-xl p-3 flex items-center justify-between" data-testid="sidebar-credits">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-amber-400 to-pink-500 flex items-center justify-center">
              <Zap size={16} className="text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Crédits IA</p>
              <p className="font-display font-bold text-sm leading-none">{user?.credits?.toLocaleString("fr-FR")}</p>
            </div>
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate("/billing")} data-testid="sidebar-topup-btn">+</Button>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-1 overflow-y-auto">
        {nav.map(({ to, label, Icon }) => {
          const active = location.pathname === to;
          return (
            <button
              key={to}
              onClick={() => { navigate(to); setMobileOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                active ? "bg-gradient-to-r from-indigo-500/20 to-cyan-500/10 text-foreground border border-primary/30" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
              data-testid={`sidebar-${to.slice(1)}`}
            >
              <Icon size={18} className={active ? "text-primary" : ""} />
              {label}
            </button>
          );
        })}
        {user?.role === "admin" && (
          <button onClick={() => { navigate("/admin"); setMobileOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-amber-500 hover:bg-amber-500/10" data-testid="sidebar-admin">
            <Shield size={18} /> Panneau Admin
          </button>
        )}
      </nav>

      <div className="p-3 border-t border-border space-y-1">
        <div className="flex items-center gap-2.5 px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate capitalize">Plan {user?.plan}</p>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={toggle} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm hover:bg-secondary transition-colors" data-testid="dashboard-theme-toggle">
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button onClick={() => { logout(); navigate("/"); }} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm text-destructive hover:bg-destructive/10 transition-colors" data-testid="logout-btn">
            <LogOut size={16} /> Quitter
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background mg-radial">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-border bg-card/40 backdrop-blur-sm fixed inset-y-0">
        <SidebarContent />
      </aside>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-card border-r border-border"><SidebarContent /></div>
          <div className="flex-1 bg-black/60" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <div className="lg:hidden h-16 flex items-center justify-between px-4 border-b border-border mg-glass sticky top-0 z-40">
          <Logo size="sm" />
          <button onClick={() => setMobileOpen(true)} className="h-9 w-9 flex items-center justify-center" data-testid="mobile-sidebar-toggle">
            <Menu size={22} />
          </button>
        </div>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
